import {CMS_ENABLED, DATOCMS_API_URL, DATOCMS_API_TOKEN, DATOCMS_ENVIRONMENT} from '@/config/envConfig'

export const isCmsEnabled = (): boolean => {
  return CMS_ENABLED === 'true'
}

export interface DatoCmsConfig {
  apiUrl: string
  apiToken: string
  environment: string
}

let cachedConfig: DatoCmsConfig | null = null

export const _resetConfigCache = () => {
  cachedConfig = null
}

export const getDatoCmsConfig = (): DatoCmsConfig | null => {
  if (!isCmsEnabled()) {
    return null
  }

  if (cachedConfig) {
    return cachedConfig
  }

  const apiUrl = DATOCMS_API_URL
  const apiToken = DATOCMS_API_TOKEN
  const environment = DATOCMS_ENVIRONMENT || 'main'

  if (!apiUrl || !apiToken) {
    return null
  }

  cachedConfig = {apiUrl, apiToken, environment}
  return cachedConfig
}

export interface DatoCmsQueryOptions {
  /**
   * Send the query as a GET so the browser can revalidate it with the response ETag. DatoCMS answers with
   * `Cache-Control: no-cache` plus an `ETag`; the browser never caches POSTs and CORS does not let scripts send
   * `If-None-Match` or read `ETag`, but GET responses are stored by the HTTP cache and revalidated automatically:
   * unchanged content comes back as a 304 served from the cached body, changed content as a new body.
   * Queries too long for a GET URL fall back to a regular POST.
   */
  cache?: boolean
}

/** Longest GET URL sent when `cache` is enabled. */
const MAX_GET_URL_LENGTH = 8000

const buildCachedGetUrl = (apiUrl: string, query: string, variables?: Record<string, unknown>): string => {
  const params = [`query=${encodeURIComponent(query.replace(/\s+/g, ' ').trim())}`]
  if (variables && Object.keys(variables).length > 0) {
    params.push(`variables=${encodeURIComponent(JSON.stringify(variables))}`)
  }

  return `${apiUrl}${apiUrl.includes('?') ? '&' : '?'}${params.join('&')}`
}

export const datoCmsQuery = async <T>(
  query: string,
  variables?: Record<string, unknown>,
  options?: DatoCmsQueryOptions,
): Promise<T | null> => {
  const config = getDatoCmsConfig()
  if (!config) return null

  const getUrl = options?.cache === true ? buildCachedGetUrl(config.apiUrl, query, variables) : null
  if (getUrl !== null && getUrl.length <= MAX_GET_URL_LENGTH) {
    try {
      const response = await fetch(getUrl, {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          Authorization: `Bearer ${config.apiToken}`,
          ...(config.environment !== 'main' ? {'X-Environment': config.environment} : {}),
        },
      })

      if (!response.ok) return null

      const json = await response.json()
      if (json.errors && json.errors.length > 0) return null

      return json.data as T
    } catch {
      return null
    }
  }

  try {
    const response = await fetch(config.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${config.apiToken}`,
        ...(config.environment !== 'main' ? {'X-Environment': config.environment} : {}),
      },
      body: JSON.stringify({query, variables}),
    })

    if (!response.ok) {
      return null
    }

    const json = await response.json()

    if (json.errors && json.errors.length > 0) {
      return null
    }

    return json.data as T
  } catch (error) {
    return null
  }
}
